import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import { toast } from "sonner";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading, Empty } from "@/components/ds/PageShell";
import { SeverityDot } from "@/components/ds/Severity";

const SEVERITIES = ["all", "critical", "high", "medium", "low"];
const SEV_LABEL = {
    all: "All",
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
};

export default function AuditFindings() {
    const { selectedEntityId, entities } = useEntity();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [regen, setRegen] = useState(false);

    const load = async () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedEntityId !== GROUP_VALUE) params.set("entity_id", selectedEntityId);
        if (filter !== "all") params.set("severity", filter);
        const { data } = await api.get(`/audit/findings?${params.toString()}`);
        setItems(data);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEntityId, filter]);

    const onRegen = async () => {
        setRegen(true);
        try {
            const { data } = await api.post("/audit/regenerate");
            toast.success(`Regenerated ${data.findings_generated} findings`);
            await load();
        } finally {
            setRegen(false);
        }
    };

    const entityName = (eid) =>
        eid ? entities.find((e) => e.id === eid)?.display_name || "—" : "Group-level";

    return (
        <PageShell width="default" testId="audit-page">
            <PageHeader
                eyebrow="Deterministic audit"
                title="Audit findings"
                subtitle="Rules-based findings calculated from the synced data. The AI layer interprets these — it cannot replace them."
                action={
                    <button
                        type="button"
                        onClick={onRegen}
                        disabled={regen}
                        className="inline-flex items-center gap-2 text-[13px] px-3 py-1.5 rounded-md text-white disabled:opacity-50"
                        style={{ background: "var(--brand)" }}
                        data-testid="audit-regen"
                    >
                        {regen ? "Running…" : "Re-run audits"}
                    </button>
                }
            />

            <div className="flex items-center gap-1.5 mb-5" data-testid="severity-filter">
                {SEVERITIES.map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setFilter(s)}
                        data-testid={`filter-${s}`}
                        className={`text-[12px] px-3 py-1.5 rounded-full font-medium transition ${
                            filter === s
                                ? s === "all"
                                    ? "bg-stone-900 text-white"
                                    : `sev-${s}`
                                : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                        }`}
                    >
                        {SEV_LABEL[s]}
                    </button>
                ))}
            </div>

            {loading ? (
                <Loading label="Loading findings…" />
            ) : items.length === 0 ? (
                <Card variant="default" padding="lg">
                    <Empty
                        title="No findings in scope"
                        hint={filter !== "all" ? `Try clearing the ${filter} filter.` : "Nothing is outstanding at this time."}
                    />
                </Card>
            ) : (
                <Section
                    title={`${items.length} finding${items.length === 1 ? "" : "s"}`}
                    subtitle="Ranked by severity. Each finding includes a recommended action."
                >
                    <div className="space-y-2">
                        {items.map((f) => (
                            <Card
                                key={f.id}
                                variant="default"
                                padding="md"
                                testId={`finding-${f.severity}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="pt-1.5">
                                        <SeverityDot level={f.severity} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                                            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">
                                                {f.category}
                                            </div>
                                            <span className="text-stone-300">·</span>
                                            <span className="text-[12px] text-stone-500">
                                                {f.is_group_level ? "Group-level" : entityName(f.entity_id)}
                                            </span>
                                            <span className="ml-auto text-[11px] text-stone-400 font-mono">
                                                {fmtDateTime(f.created_at)}
                                            </span>
                                        </div>
                                        <div className="text-[15px] font-medium text-stone-900 leading-snug">
                                            {f.title}
                                        </div>
                                        <p className="text-[13.5px] text-stone-600 mt-1 leading-relaxed">
                                            {f.explanation}
                                        </p>
                                        <div className="text-[13px] text-stone-500 mt-2">
                                            <span className="text-stone-700 font-medium">Recommended:</span>{" "}
                                            {f.recommended_action}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </Section>
            )}
        </PageShell>
    );
}
